import { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import { AppTracker, EventType, UserActionType } from 'types';
import { getCallbackTriggersMap, getEventURL, redirectToPaymeCheckout, UZBEKITTV_FEE_IN_UZS } from 'utils';
import { authSelector, callbackTriggersSelector } from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { tracker } from 'services';
import { useStore } from 'hooks';
import { Button, Text } from 'components';
import { CloseIcon } from 'components/Icon';

import styles from './BecomeMemberModal.module.scss';

export type BecomeMemberModalProps = {
  channelName: string;
  eventType: EventType;
  eventCode: string;
  isOpen?: boolean;
  closable?: boolean;
  onClose?(): void;
  onAuthRequired?(): void;
};

const BecomeMemberModal: FC<BecomeMemberModalProps> = ({
  channelName,
  eventType,
  eventCode,
  isOpen = false,
  closable = false,
  onClose,
  onAuthRequired
}): JSX.Element => {
  const dispatch = useDispatch();
  const auth = useStore(authSelector);
  const callbackTriggers = useStore(callbackTriggersSelector);

  // redirect to payme after login
  useEffect(() => {
    if (auth.isAuth && callbackTriggers.length) {
      const callbackTriggersMap = getCallbackTriggersMap(callbackTriggers);

      if (callbackTriggersMap[UserActionType.PAYME_ATTEMPTED]) {
        dispatch(callbackTriggersActions.remove(UserActionType.PAYME_ATTEMPTED));

        redirectToPayme();
      }
    }
  }, []);

  const handleOnClose = () => {
    if (!closable) return;

    tracker.closeBecomeMemberModal({
      authenticated: auth.isAuth,
      eventType,
      eventCode
    });

    onClose?.();
  };

  const redirectToPayme = () => {
    tracker.proceedToPayme({ source: AppTracker.BecomeMemberWithPaymeSource.MODAL, eventType, eventCode });

    redirectToPaymeCheckout({
      amount: UZBEKITTV_FEE_IN_UZS,
      callbackUrl: getEventURL(channelName, eventCode, true),
      attributes: {
        username: auth.username,
        name: auth.name,
        channelName
      }
    });
  };

  const handleOnPurchase = () => {
    const { isAuth } = auth;

    tracker.becomeMemberWithPayme({
      source: AppTracker.BecomeMemberWithPaymeSource.MODAL,
      authenticated: isAuth,
      eventType,
      eventCode
    });

    if (!isAuth) {
      onAuthRequired?.();
    } else {
      redirectToPayme();
    }
  };

  return createPortal(
    <div className={cx(styles.wrapper, { [styles.open]: isOpen })}>
      <div className={styles.overlay} />
      <div className={styles.container}>
        {closable && (
          <div className={styles.close} onClick={handleOnClose} role="button" tabIndex={0}>
            <CloseIcon width="20px" height="20px" />
          </div>
        )}
        <div className={styles.content}>
          <Text size="h2" weight="bold" className={styles.title}>
            Nega obuna bo'lish kerak?
          </Text>

          <ul className={styles.features}>
            <li>
              <Text size="h4" className={styles.feature}>
                <span className="mr-8">🙌</span>
                o'zingizni qiziqtirgan savollarni to'g'ridan to'gri so'raysiz
              </Text>
            </li>
            <li>
              <Text size="h4" className={styles.feature}>
                <span className="mr-8">💬</span>
                qizg'in davra suhbatlarida ishtirok etasiz
              </Text>
            </li>
            <li>
              <Text size="h4" className={styles.feature}>
                <span className="mr-8">📺</span>
                o'tkazib yuborilgan ko'rsatuvlarni qayta tomosha qilasiz
              </Text>
            </li>
          </ul>

          <Text size="h3" className={styles.mention}>
            Shanba va yakshanba kunlari suhbatlarimizni ko'ring va ishtirok eting
          </Text>

          <Button block className={styles.purchase} onClick={handleOnPurchase}>
            <span>Obuna bo'lish</span>
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BecomeMemberModal;
